import { useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import {
  getNamespacedSocket,
  disconnectNamespacedSocket,
} from '../api/socket';

const NAMESPACE = '/tracking';

/** Collapse a burst of events (e.g. sealing a 30-box bag) into one refetch. */
const REFETCH_DEBOUNCE_MS = 500;

export interface TrackingSocketState {
  /** True while the live socket is connected — drives the "Live" badge. */
  connected: boolean;
}

/**
 * Subscribe the open public journey to live updates.
 *
 * Pass the resolved journey's `type` ("BOX" | "AWB" | "BAG" | "TRIP" |
 * "MAWB") and `entityId`, plus an `onUpdate` callback. The backend resolves
 * the whole shipment family and pushes a `tracking:event` signal whenever
 * anything in that family changes — including when staff flip an event
 * between Public and Internal. We react by re-running the tracking fetch, so
 * the timeline refreshes without a manual reload. The backend stays the
 * source of truth: we refetch through the same public endpoint rather than
 * splicing events client-side.
 */
export function useTrackingSocket(
  type: string | null,
  entityId: string | null,
  onUpdate: () => void,
): TrackingSocketState {
  const [connected, setConnected] = useState(false);

  // Latest watch target, read by the `connect` handler so a reconnect
  // re-subscribes to whatever journey is currently on screen.
  const targetRef = useRef<{ type: string; id: string } | null>(null);
  targetRef.current = type && entityId ? { type, id: entityId } : null;

  // Keep the callback in a ref so the connection effect can stay mount-once
  // while always calling the freshest handler.
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const socketRef = useRef<Socket | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Connection lifecycle — mounts once.
  useEffect(() => {
    const socket = getNamespacedSocket(NAMESPACE);
    socketRef.current = socket;
    if (!socket.connected) socket.connect();

    const subscribeNow = () => {
      if (targetRef.current) socket.emit('subscribe', targetRef.current);
    };
    const onConnect = () => {
      setConnected(true);
      subscribeNow();
    };
    const onDisconnect = () => setConnected(false);
    const onEvent = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onUpdateRef.current();
      }, REFETCH_DEBOUNCE_MS);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('tracking:event', onEvent);

    if (socket.connected) {
      setConnected(true);
      subscribeNow();
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('tracking:event', onEvent);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      disconnectNamespacedSocket(NAMESPACE);
      socketRef.current = null;
    };
  }, []);

  // Re-subscribe whenever the watched journey changes (new search).
  useEffect(() => {
    const socket = socketRef.current;
    if (socket?.connected && targetRef.current) {
      socket.emit('subscribe', targetRef.current);
    }
  }, [type, entityId]);

  return { connected };
}
